/**
 * What the signed-in person may open.
 *
 * One query, shared by the navigation and by any page that hides a control behind a
 * permission. Cached for the session: permissions change rarely, and when an
 * administrator edits their own row the editor invalidates this key explicitly.
 *
 * This decides what to *render*. The API decides what to *allow*, on every request,
 * and does not consult this. Hiding a link is a courtesy to the person who could not
 * have used it; it is never the thing that stops them.
 */

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/features/auth/AuthProvider';
import { api, ApiError, type PermissionLevel, type Section } from '@/lib/api';

const RANK: Record<PermissionLevel, number> = { none: 0, read: 1, write: 2 };

export function usePermissions() {
  const query = useQuery({
    queryKey: ['myPermissions'],
    queryFn: api.myPermissions,
    staleTime: 5 * 60 * 1000,
  });

  const grid = query.data?.permissions;

  /*
   * A sign-in that succeeded at Cognito but has no portal door. The API refuses every
   * request with PORTAL_ACCESS; the honest thing is to end the session with a message
   * that says so, rather than show an empty shell that errors on every panel.
   */
  const { signOut } = useAuth();
  useEffect(() => {
    const error = query.error;
    if (error instanceof ApiError && error.details?.code === 'PORTAL_ACCESS') void signOut('noAccess');
  }, [query.error, signOut]);

  /**
   * Optimistic while loading: `true` until the grid arrives.
   *
   * The alternative — hiding every link for the half-second before the first response
   * — makes the sidebar visibly assemble itself on each load, and a nav that flickers
   * into existence reads as a bug. Nothing is exposed by this: a page rendered before
   * its permission is known still gets a 403 from the API if it was not allowed.
   */
  const can = (section: Section, level: PermissionLevel): boolean => {
    if (!grid) return true;
    return RANK[grid[section]] >= RANK[level];
  };

  return { can, loaded: Boolean(grid), role: query.data?.role };
}
